import { HttpClient } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { GeneratorUnit, HierarchyRegistrationRow } from '../../../models/api-response.model';

type GenerationUnitsMock = {
  header: {
    title: string;
    description: string;
  };
  utilityRegistrations: HierarchyRegistrationRow[];
  units: GeneratorUnit[];
};

const DEFAULT_DATA: GenerationUnitsMock = {
  header: {
    title: 'Manage Generation Units',
    description: 'Create, review, and link generation units to generator registrations.'
  },
  utilityRegistrations: [],
  units: []
};

@Component({
  selector: 'app-generation-units',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './generation-units.component.html',
  styleUrls: ['./generation-units.component.scss']
})
export class GenerationUnitsComponent {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);

  readonly pageData = signal<GenerationUnitsMock>(DEFAULT_DATA);
  readonly searchTerm = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly isFormOpen = signal(false);
  readonly editingUnitId = signal<number | null>(null);
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);

  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly title = computed(() => this.pageData().header.title);
  readonly description = computed(() => this.pageData().header.description);
  readonly utilityRegistrations = computed(() => this.pageData().utilityRegistrations);
  readonly filteredUnits = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    return this.pageData().units.filter((unit) => {
      if (!search) {
        return true;
      }
      return [
        unit.unique_no,
        unit.model,
        unit.name_of_developer,
        this.generatorLabel(unit.generator_registration_id || null),
        unit.purchase_mode
      ].some((value) => (value || '').toLowerCase().includes(search));
    });
  });
  readonly totalItems = computed(() => this.filteredUnits().length);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  readonly paginatedUnits = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredUnits().slice(start, start + this.pageSize());
  });
  readonly showingStart = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  readonly showingEnd = computed(() => (this.totalItems() === 0 ? 0 : Math.min(this.currentPage() * this.pageSize(), this.totalItems())));

  readonly form = this.fb.group({
    unique_no: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
    model: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(120)]],
    scada_turbine_gut_inverter_no: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]],
    installed_capacity_mw: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
    latitude: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
    longitude: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
    make: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(120)]],
    name_of_developer: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(160)]],
    identifier_tag: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]],
    purchase_mode: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
    utility_name: [''],
    consumer_utility_name: [''],
    consumer_name: [''],
    contracted_capacity_mw: [''],
    contract_rate_per_kwh: [''],
    contract_from_date: [''],
    contract_to_date: [''],
    contract_from_time_block: [''],
    contract_to_time_block: [''],
    remarks: [''],
    generator_registration_id: [null as number | null]
  });

  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.http.get<GenerationUnitsMock>('json/generation-units.mock.json').subscribe({
      next: (data) => this.pageData.set(data),
      error: () => {
        this.pageData.set(DEFAULT_DATA);
        this.showToast('Unable to load mock generation units data.', 'error');
      }
    });
  }

  openCreateForm(): void {
    this.showToast(null);
    this.editingUnitId.set(null);
    this.form.reset({
      unique_no: '',
      model: '',
      scada_turbine_gut_inverter_no: '',
      installed_capacity_mw: '',
      latitude: '',
      longitude: '',
      make: '',
      name_of_developer: '',
      identifier_tag: '',
      purchase_mode: '',
      utility_name: '',
      consumer_utility_name: '',
      consumer_name: '',
      contracted_capacity_mw: '',
      contract_rate_per_kwh: '',
      contract_from_date: '',
      contract_to_date: '',
      contract_from_time_block: '',
      contract_to_time_block: '',
      remarks: '',
      generator_registration_id: null
    });
    this.isFormOpen.set(true);
  }

  edit(unit: GeneratorUnit): void {
    this.showToast(null);
    this.editingUnitId.set(unit.id);
    this.form.reset({
      unique_no: unit.unique_no,
      model: unit.model,
      scada_turbine_gut_inverter_no: unit.scada_turbine_gut_inverter_no,
      installed_capacity_mw: unit.installed_capacity_mw,
      latitude: unit.latitude,
      longitude: unit.longitude,
      make: unit.make,
      name_of_developer: unit.name_of_developer,
      identifier_tag: unit.identifier_tag,
      purchase_mode: unit.purchase_mode,
      utility_name: unit.utility_name || '',
      consumer_utility_name: unit.consumer_utility_name || '',
      consumer_name: unit.consumer_name || '',
      contracted_capacity_mw: unit.contracted_capacity_mw || '',
      contract_rate_per_kwh: unit.contract_rate_per_kwh || '',
      contract_from_date: unit.contract_from_date || '',
      contract_to_date: unit.contract_to_date || '',
      contract_from_time_block: unit.contract_from_time_block || '',
      contract_to_time_block: unit.contract_to_time_block || '',
      remarks: unit.remarks || '',
      generator_registration_id: unit.generator_registration_id || null
    });
    this.isFormOpen.set(true);
  }

  closeForm(): void {
    this.isFormOpen.set(false);
    this.editingUnitId.set(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    const nextUnit: GeneratorUnit = {
      id: this.editingUnitId() ?? this.nextUnitId(),
      unique_no: rawValue.unique_no || '',
      model: rawValue.model || '',
      scada_turbine_gut_inverter_no: rawValue.scada_turbine_gut_inverter_no || '',
      installed_capacity_mw: rawValue.installed_capacity_mw || '',
      latitude: rawValue.latitude || '',
      longitude: rawValue.longitude || '',
      make: rawValue.make || '',
      name_of_developer: rawValue.name_of_developer || '',
      identifier_tag: rawValue.identifier_tag || '',
      purchase_mode: rawValue.purchase_mode || '',
      utility_name: rawValue.utility_name || null,
      consumer_utility_name: rawValue.consumer_utility_name || null,
      consumer_name: rawValue.consumer_name || null,
      contracted_capacity_mw: rawValue.contracted_capacity_mw || null,
      contract_rate_per_kwh: rawValue.contract_rate_per_kwh || null,
      contract_from_date: rawValue.contract_from_date || null,
      contract_to_date: rawValue.contract_to_date || null,
      contract_from_time_block: rawValue.contract_from_time_block || null,
      contract_to_time_block: rawValue.contract_to_time_block || null,
      remarks: rawValue.remarks || null,
      generator_registration_id: rawValue.generator_registration_id || null,
      created_at: null,
      updated_at: null,
      is_deleted: false
    };

    const existingId = this.editingUnitId();
    const duplicateUnit = this.pageData().units.some((unit) =>
      unit.unique_no.toLowerCase() === nextUnit.unique_no.toLowerCase() && unit.id !== existingId
    );

    if (duplicateUnit) {
      this.showToast('Unique No. already exists.', 'error');
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      units: existingId === null
        ? [nextUnit, ...data.units]
        : data.units.map((unit) => unit.id === existingId ? nextUnit : unit)
    }));

    this.showToast(existingId === null ? 'Generator unit created successfully.' : 'Generator unit updated successfully.', 'success');
    this.closeForm();
    this.currentPage.set(1);
  }

  remove(unit: GeneratorUnit): void {
    if (!confirm(`Delete unit "${unit.unique_no}"?`)) {
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      units: data.units.filter((item) => item.id !== unit.id)
    }));
    this.showToast('Generator unit deleted successfully.', 'success');
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  generatorLabel(generatorRegistrationId: number | null | undefined): string {
    if (!generatorRegistrationId) {
      return '-';
    }
    return this.utilityRegistrations().find((registration) => registration.id === generatorRegistrationId)?.entity_name || '-';
  }

  private showToast(message: string | null, type: 'success' | 'error' = 'success'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    if (!message) {
      this.toast.set(null);
      return;
    }
    this.toast.set({ message, type });
    this.toastTimer = setTimeout(() => this.toast.set(null), 3000);
  }

  private nextUnitId(): number {
    return this.pageData().units.reduce((max, unit) => Math.max(max, unit.id), 0) + 1;
  }
}
